<script>
import LogoComponent from '../Logo';
import {
  iIcon,
  iTab,
  iTabs,
  iTabsContainerTypes,
  iTabsOrientation,
  iTabsSize,
  openModal,
} from '@inscribe/signal';
import UserArea from './UserArea';
import SearchModal from './search/SearchModal.vue';
import { mapGetters, mapActions } from 'vuex';

export default {
  components: {
    iIcon,
    iTab,
    iTabs,
    LogoComponent,
    UserArea,
    SearchModal,
  },
  props: {
    screenSize: {
      type: String,
      default: null,
    },
  },
  data: () => ({
    hoveredItem: null,
    selectedItem: null,
    currentWidth: 64,
    iTabsContainerTypes,
    iTabsOrientation,
    iTabsSize,
    menuContainerWidth: 64,
    firstLayerOpen: false,
    hoverTimeout: 0,
    hoverTimeoutAnimate: 0,
    animateMenu: false,
    preventMouseHover: false,
    secondLayerVisibleMenuItems: [],
  }),
  computed: {
    ...mapGetters([
      'collectEnabled',
      'canViewBusinessComplianceAgent',
      'canViewCustomerFraudReasoningAgent',
      'canUseCredit',
      'unifiedAgentEnabled',
      'getCachedCustomerById',
      'isChatAgentEnabled',
    ]),
    currentPath() {
      return this.$route.path;
    },
    customerId() {
      return this.$route.params.customer_id;
    },
    secondLayerVisibleMenuItemsHash() {
      // We want to rerender the second layer menu when the visible menu items
      // change, so we keep track of the hashed labels as a key.
      const menuItemLabels = this.secondLayerVisibleMenuItems.map(
        (item) => item.label,
      );
      return menuItemLabels.join(',');
    },
    hasSecondLayerMenu() {
      return (
        this.screenSizeHasMenu &&
        !!this.$route.matched.find((route) =>
          [
            'customer-wrapper',
            'customer-overview',
            'customer-risk-analysis',
            'decision-engine-checks',
            'decision-engine-rules',
            'decision-engine-detectors',
            'decision-engine-blocklist',
            'decision-engine-checks-edit',
            'decision-engine-rule-edit',
            'decision-engine-blocklist-edit',
            'decision-engine-signals-edit',
            'account-settings-view',
            'team-settings-view',
            'api-settings-list-keys',
            'api-settings-create-key',
            'api-settings-edit-key',
            'api-settings-view-key',
            'webhooks-settings-list',
            'webhooks-settings-create',
            'webhooks-settings-edit',
            'notifications-settings-view',
            'saml-settings-view',
            'saml-settings-new-identity-provider',
            'saml-settings-edit-identity-provider-view',
            'assistant-settings-view',
            'collect-config-settings-view',
            'collect-config-email-customization',
            'collect-config-portal-customization',
          ].includes(route.name),
        )
      );
    },
    screenSizeHasMenu() {
      return this.screenSize && !['small1'].includes(this.screenSize);
    },
    firstLayerCollapsed() {
      return (
        !this.screenSizeHasMenu ||
        (this.hasSecondLayerMenu && !this.firstLayerOpen)
      );
    },
    documentsCount() {
      return this.getCachedCustomerById(this.customerId)?.no_of_documents || 0;
    },
    getBadgeValue() {
      return (item) => {
        let badge;
        if (item.id === 'documents') {
          badge = this.documentsCount;
        }
        return badge;
      };
    },
    selectedTabIndex() {
      const selectedIndex = this.secondLayerVisibleMenuItems.findIndex(
        (subItem) => {
          const pathsToCheck = [subItem.navigationRoutePath];
          if (Array.isArray(subItem.detectRoutePaths)) {
            pathsToCheck.push(...subItem.detectRoutePaths);
          }
          for (const path of pathsToCheck) {
            const resolvedPath = path.replace(/{customerId}/g, this.customerId);
            if (
              this.currentPath === resolvedPath ||
              this.currentPath.startsWith(resolvedPath + '/')
            ) {
              return true;
            }
          }
          return false;
        },
      );
      return selectedIndex === -1 ? 0 : selectedIndex;
    },
    menuItems() {
      const items = [
        {
          id: 'customers',
          icon: 'medium-user-multiple',
          label: 'Customers',
          detectRoutePaths: ['/customers', '/customer'],
          navigationRoutePath: '/customers',
          secondLevelMenuTitle: 'Customer',
          secondLevelMenuItems: [
            {
              id: 'fraud-reasoning',
              name: 'customer-fraud-reasoning',
              label: 'Fraud Reasoning',
              navigationRoutePath: '/customer/{customerId}/fraud-reasoning',
              visible: this.canViewCustomerFraudReasoningAgent,
            },
            {
              id: 'risk-analysis',
              name: 'customer-risk-analysis',
              label: 'Risk Analysis',
              navigationRoutePath: '/customer/{customerId}/risk-analysis',
              visible: this.unifiedAgentEnabled,
            },
            {
              id: 'overview',
              name: 'customer-overview',
              label: 'Overview',
              navigationRoutePath: '/customer/{customerId}/overview',
              visible:
                !this.unifiedAgentEnabled &&
                (this.canViewBusinessComplianceAgent || this.canUseCredit),
            },
            {
              id: 'customer-fraud-analyst',
              name: 'customer-fraud-analyst',
              label: 'Fraud',
              navigationRoutePath: '/customer/{customerId}/fraud',
              visible: !this.unifiedAgentEnabled,
            },
            {
              id: 'customer-compliance',
              name: 'customer-compliance',
              label: 'Compliance',
              navigationRoutePath: '/customer/{customerId}/compliance',
              detectRoutePaths: ['/customer/{customerId}/compliance-report'],
              visible: !this.unifiedAgentEnabled,
            },
            {
              id: 'credit',
              name: 'customer-credit',
              label: 'Credit Insights',
              navigationRoutePath: '/customer/{customerId}/credit',
              detectRoutePaths: [
                '/customer/{customerId}/credit/loan',
                '/customer/{customerId}/credit/income',
              ],
              visible: true,
            },
            {
              id: 'customer-profile',
              name: 'customer-profile',
              label: 'Profile',
              navigationRoutePath: '/customer/{customerId}/profile',
              visible: true,
            },
            {
              id: 'documents',
              label: 'Documents',
              name: 'customer-documents',
              navigationRoutePath: '/customer/{customerId}/documents',
              visible: true,
            },
            {
              id: 'transactions',
              name: 'customer-credit-transactions',
              label: 'Transactions',
              navigationRoutePath: '/customer/{customerId}/credit-transactions',
              detectRoutePaths: [
                '/customer/{customerId}/credit-transactions-report',
              ],
              visible: true,
            },
          ],
        },
        {
          id: 'documents',
          icon: 'medium-file',
          label: 'Documents',
          detectRoutePaths: ['/documents'],
          navigationRoutePath: '/documents',
        },
        {
          id: 'decision-engine',
          icon: 'medium_workflow_1',
          label: 'Decision Engine',
          detectRoutePaths: ['/decision-engine'],
          navigationRoutePath: '/decision-engine',
          secondLevelMenuTitle: 'Decision Engine',
          secondLevelMenuItems: [
            {
              id: 'decision-engine-detectors',
              name: 'decision-engine-detectors',
              label: 'Insights',
              navigationRoutePath: '/decision-engine/detectors',
              detectRoutePaths: [
                '/decision-engine/signals-edit',
                '/decision-engine/detectors',
              ],
            },
            {
              id: 'decision-engine-rules',
              name: 'decision-engine-rules',
              label: 'Rules',
              navigationRoutePath: '/decision-engine/rules',
              detectRoutePaths: ['/decision-engine/rules-edit'],
            },
            {
              id: 'decision-engine-checks',
              name: 'decision-engine-checks',
              label: 'Requirements',
              navigationRoutePath: '/decision-engine/checks',
              detectRoutePaths: ['/decision-engine/checks-edit'],
            },
            {
              id: 'decision-engine-blocklist',
              name: 'decision-engine-blocklist',
              label: 'Blocklist',
              navigationRoutePath: '/decision-engine/blocklist',
              detectRoutePaths: ['/decision-engine/blocklist-edit'],
            },
          ],
        },
        {
          id: 'analytics',
          icon: 'medium_money_graph_pie_chart',
          label: 'Analytics',
          detectRoutePaths: ['/analytics'],
          navigationRoutePath: '/analytics',
        },
        {
          id: 'settings',
          icon: 'medium-setting-cog',
          label: 'Settings',
          detectRoutePaths: ['/settings'],
          navigationRoutePath: '/settings',
          secondLevelMenuTitle: 'Settings',
          secondLevelMenuItems: [
            {
              id: 'settings-account',
              label: 'Account',
              navigationRoutePath: '/settings/account',
            },
            {
              id: 'settings-team',
              label: 'Team',
              navigationRoutePath: '/settings/team',
            },
            {
              id: 'settings-collect',
              label: 'Collect',
              navigationRoutePath: '/settings/collect',
              visible: this.collectEnabled,
            },
            {
              id: 'settings-api',
              label: 'API Keys',
              navigationRoutePath: '/settings/api',
            },
            {
              id: 'settings-webhooks',
              label: 'Webhooks',
              navigationRoutePath: '/settings/webhooks',
            },
            {
              id: 'settings-notifications',
              label: 'Notifications',
              navigationRoutePath: '/settings/notifications',
            },
            {
              id: 'settings-saml',
              label: 'Single Sign-On',
              navigationRoutePath: '/settings/saml',
            },
            {
              id: 'settings-assistant',
              label: 'Assistant',
              navigationRoutePath: '/settings/assistant',
              visible: this.isChatAgentEnabled,
            },
          ],
        },
      ];
      if (this.collectEnabled) {
        items.splice(3, 0, {
          id: 'collect',
          icon: 'medium-file-request',
          label: 'Collect',
          detectRoutePaths: ['/collect'],
          navigationRoutePath: '/collect',
        });
      }

      return items;
    },
    globalSearchShortcutText() {
      const isMac =
        (navigator.userAgentData?.platform ?? '')
          .toLowerCase()
          .includes('mac') || /mac/i.test(navigator.userAgent);
      return isMac ? '⌘ K' : 'Ctrl K';
    },
  },
  watch: {
    // watchers to detect when the collectEnabled flags change,
    // as they will initially be undefined and we need to detect the selected item
    collectEnabled(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.detectSelectedItem();
      }
    },
    canViewBusinessComplianceAgent(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.detectSelectedItem();
      }
    },
    isChatAgentEnabled(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.detectSelectedItem();
      }
    },
    $route() {
      this.detectSelectedItem();
      this.setMenuWidth();

      this.onFirstLayerHover(false);
      this.preventMouseHover = true;
      setTimeout(() => {
        this.preventMouseHover = false;
      }, 500);
    },
    screenSize() {
      this.setMenuWidth();
    },
  },
  mounted() {
    this.detectSelectedItem();
    this.setMenuWidth();
    window.addEventListener('resize', this.setMenuWidth);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.setMenuWidth);
  },
  methods: {
    setMenuWidth() {
      this.currentWidth = 64;
      if (this.screenSizeHasMenu) {
        this.currentWidth = this.hasSecondLayerMenu ? 264 : 240;
      }
      this.$emit('left-nav-size-change', this.currentWidth);
    },
    detectSelectedItem() {
      this.menuItems.forEach((item) => {
        if (!item.detectRoutePaths) return;
        item.detectRoutePaths.forEach((path) => {
          if (this.currentPath?.startsWith(path)) {
            this.selectedItem = item;
          }
        });
      });
      if (this.selectedItem?.secondLevelMenuItems) {
        this.setSecondLayerVisibleMenuItems(
          this.selectedItem.secondLevelMenuItems,
        );
      }
    },
    openCustomersPage() {
      this.onItemClick({
        navigationRoutePath: '/customers',
      });
    },
    onFirstLayerHover(value) {
      if (this.preventMouseHover) {
        return;
      }
      this.hoverTimeout && clearTimeout(this.hoverTimeout);
      this.hoverTimeout = setTimeout(() => {
        this.hoverTimeoutCallback(value);
      }, 50);
    },
    hoverTimeoutCallback(value) {
      this.animateMenu = true;
      this.menuContainerWidth = value ? 240 : 64;
      this.firstLayerOpen = value;
      this.setMenuWidth();
      this.hoverTimeoutAnimate && clearTimeout(this.hoverTimeoutAnimate);
      this.hoverTimeoutAnimate = setTimeout(() => {
        this.animateMenu = false;
      }, 151);
    },
    onSecondMenuClick(selection) {
      const item = this.secondLayerVisibleMenuItems[selection];
      this.onItemClick(item);
    },
    onItemClick(item) {
      if (
        item.navigationRoutePath &&
        !this.currentPath.startsWith(item.navigationRoutePath)
      ) {
        this.$router
          .push({
            path: item.navigationRoutePath.replace(
              /{customerId}/g,
              this.customerId,
            ),
          })
          .catch(() => {});
      }
    },
    setSecondLayerVisibleMenuItems(items) {
      this.secondLayerVisibleMenuItems = items.filter(
        (item) => item.visible !== false,
      );
    },
    openAndFocusGlobalSearchModal() {
      openModal('global-search-modal');
      setTimeout(() => {
        const searchInput = document.querySelector(
          '[data-modal-id="global-search-modal"] #searchInput',
        );
        if (searchInput) {
          searchInput.focus();
        }
      }, 50);
    },
  },
};
</script>

<template>
  <div
    :style="{ width: currentWidth + 'px' }"
    :class="{
      'one-layer': !hasSecondLayerMenu,
      'two-layers': hasSecondLayerMenu,
    }"
    class="left-nav d-flex"
  >
    <search-modal />

    <div class="layers">
      <!-- main first layer menu -->
      <div
        :style="{
          width: hasSecondLayerMenu ? menuContainerWidth + 'px' : '100%',
        }"
        :class="{
          open: firstLayerOpen,
          'with-animation': animateMenu,
        }"
        class="layer first d-flex flex-column i-p-6"
        @mouseover="onFirstLayerHover(true)"
        @mouseleave="onFirstLayerHover(false)"
      >
        <!-- logo container-->
        <button
          :class="{
            'justify-content-center': !firstLayerOpen,
            'full-size': !firstLayerCollapsed,
            'with-animation': animateMenu,
          }"
          class="logo-container i-mt-8 d-flex"
          aria-label="Main nav: Go home"
          data-dd-action-name="main-nav-home"
          @click="openCustomersPage"
        >
          <logo-component height="21px" />
        </button>

        <!-- Search button -->
        <button
          :class="{
            collapsed: firstLayerCollapsed,
            'justify-content-center': firstLayerCollapsed,
            'with-animation': animateMenu,
          }"
          class="menu-item-btn search-button d-flex flex-row align-items-center i-radius-xl i-py-5 i-px-6 i-gap-6 i-mt-8"
          aria-label="Main nav: Search"
          data-dd-action-name="main-nav-search"
          @click="openAndFocusGlobalSearchModal"
        >
          <div class="content d-flex flex-row align-items-center">
            <i-icon symbol="medium-search" :size="16" class="menu-icon" />
            <div class="label i-text-subheading-2">Search</div>
            <div class="shortcut i-radius-sm">
              {{ globalSearchShortcutText }}
            </div>
          </div>
        </button>

        <!-- menu container -->
        <div
          class="d-flex flex-column justify-content-start flex-grow-1 i-gap-1 i-mt-9"
        >
          <button
            v-for="item in menuItems"
            :key="item.label"
            :class="{
              collapsed: firstLayerCollapsed,
              selected: selectedItem?.id === item.id,
              'justify-content-center': firstLayerCollapsed,
              'with-animation': animateMenu,
            }"
            class="menu-item-btn d-flex align-items-center i-py-8"
            :aria-label="`Main nav: ${item.label}`"
            :data-dd-action-name="`main-nav-${item.id}`"
            :data-testid="'left-nav-item-' + item.id"
            @click="() => onItemClick(item)"
            @mouseover="hoveredItem = item"
            @mouseleave="hoveredItem = null"
          >
            <div class="content d-flex flex-row align-items-center">
              <i-icon
                :symbol="item.icon"
                :size="16"
                :colour="
                  selectedItem?.id === item.id
                    ? `var(--icon-primary)`
                    : hoveredItem?.id === item.id
                      ? `var(--icon-neutral)`
                      : `var(--icon-neutral)`
                "
                class="menu-icon"
              ></i-icon>
              <div class="label i-text-subheading-2">
                {{ item.label }}
              </div>
            </div>
          </button>
        </div>

        <!-- user area -->
        <user-area class="user-container" :collapsed="firstLayerCollapsed" />
      </div>

      <!-- second layer menu -->
      <div
        v-if="hasSecondLayerMenu && selectedItem"
        class="layer second d-flex flex-column i-py-8 i-px-6"
        :class="selectedItem.id"
      >
        <div
          class="label i-text-heading-5 i-px-6 i-mb-8"
          data-dd-action-name="left-nav-second-layer-menu-title"
        >
          {{ selectedItem?.secondLevelMenuTitle }}
        </div>
        <i-tabs
          :key="secondLayerVisibleMenuItemsHash"
          :type="iTabsContainerTypes.BUTTONS"
          :orientation="iTabsOrientation.VERTICAL"
          :preselected-tab="selectedTabIndex"
          :size="iTabsSize.SMALL"
          @tab-selected="onSecondMenuClick"
        >
          <template #tabs>
            <i-tab
              v-for="item in secondLayerVisibleMenuItems"
              :key="item.label"
              :label="item.label"
              :aria-label="`Sub nav: ${item.label}`"
              :data-dd-action-name="`sub-nav-${item.id}`"
              :badge="getBadgeValue(item)"
              :data-testid="'left-nav-sub-item-' + item.id"
              class="sub-item"
              @click="() => onItemClick(item)"
            />
          </template>
        </i-tabs>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.left-nav {
  background: var(--background-neutral);
  height: 100%;
  border-right: 1px solid var(--border-neutral);
  width: 100%;
  box-sizing: content-box;

  &.one-layer {
    border-right: 0;

    .layers {
      width: 100%;
      .layer {
        width: 100%;
      }
    }
  }

  &.two-layers {
    .first.open {
      border-right: 1px solid var(--border-neutral);
    }
  }

  .layers {
    position: relative;
    height: 100%;

    .layer {
      position: absolute;
      top: 0;
      left: 0;
      bottom: 0;
      height: 100%;

      &.first {
        min-width: 64px;
        background: var(--background-neutral);
        z-index: 1002;

        &.with-animation {
          transition: width 150ms ease-in-out;
        }
      }

      &.second {
        background: var(--base);
        z-index: 1001;
        margin-left: 64px;
        width: 200px;

        &.decision-engine,
        &.settings {
          .label {
            margin-bottom: 24px;
            padding-top: 8px;
          }
        }

        .label {
          color: var(--text-neutral-bold);
        }

        .sub-item {
          border-radius: 12px;

          &.i-tab {
            :deep(.label) {
              color: var(--text-neutral-bold);
            }
          }

          &:hover {
            background-color: var(--surface-neutral-subtle);
            :deep(.badge-container) {
              background-color: var(--surface-neutral);
            }
          }

          &.selected {
            background-color: var(--surface-primary-subtle);

            :deep(.badge-container) {
              background-color: var(--surface-primary);
            }
          }
        }
      }
    }
  }

  .logo-container {
    position: relative;
    overflow: hidden;
    cursor: pointer;
    background: none;
    border: none;
    padding: 0;

    &:focus-visible {
      outline: 2px solid var(--border-primary);
      outline-offset: 2px;
      border-radius: 4px;
    }
    width: 12px;
    height: 30px;
    margin-left: 12px;

    &.with-animation {
      transition: width 150ms ease-in-out;
    }

    &.full-size {
      width: 120px !important;
    }

    svg {
      position: absolute;
      left: 0;
      top: 0;
      width: 109px;
      height: 24px;
    }
  }

  .menu-item-btn {
    position: relative;
    background-color: var(--surface-neutral-subtle);
    height: 36px;
    min-width: 40px;
    width: 214px;
    border: 0;
    border-radius: 12px;
    outline: 0;
    box-sizing: border-box;
    overflow: hidden;
    transition: all 150ms ease-in-out;

    &.with-animation {
      transition:
        background-color 150ms ease-in-out,
        width 150ms ease-in-out;
    }

    &.collapsed {
      width: 40px;
    }

    .menu-icon {
      position: absolute;
      left: 12px;
    }

    .label {
      position: absolute;
      left: 40px;
      text-wrap: nowrap;
      color: var(--text-neutral-bold);
    }

    &:hover {
      background-color: var(--base);
    }

    &.selected {
      background-color: var(--base);
      box-shadow: 0px 0px 24px 0px rgba(0, 0, 0, 0.04);
    }
  }

  .search-button {
    background-color: var(--base);
    border: 1px solid var(--border-neutral);
    transition: border 150ms ease-in-out;

    .menu-icon {
      margin-left: -1px;
      color: var(--icon-neutral);
    }

    &.label {
      transition: color 150ms ease-in-out;
    }

    &.collapsed {
      .shortcut {
        display: none;
      }
    }

    .shortcut {
      background-color: var(--surface-neutral-subtle);
      padding: 2px 4px;
      font-size: 12px;
      color: var(--text-neutral);
      position: absolute;
      right: 8px;
      top: 8px;
    }

    &:hover {
      border: 1px solid var(--border-neutral-subtle);

      .label {
        color: var(--text-neutral-bold);
      }
    }
  }

  .user-container {
    position: absolute;
    bottom: 16px;
    left: 16px;
    right: 16px;
  }
}
</style>
